export type ErrorOrigin = 'server' | 'client';

/**
 * Critical application error that should be caught by error boundaries
 * and reported to monitoring services like Grafana.
 *
 * `innerError` is deliberately kept as a plain property and NOT concatenated
 * onto `this.stack`. An earlier version appended it as a
 * `\n\nInner Error: <stack>` trailer, which corrupted the trace in Grafana:
 * Faro's stack-trace parser extracts only `at ...` frame lines from `.stack`
 * and silently discards every other line, so the "Inner Error:" header was
 * dropped and the inner error's frames were flattened into the wrapper's own
 * frame list with nothing marking the boundary. The result read as real
 * application function names randomly interleaved between blocks of minified
 * react-dom internals.
 *
 * Error boundaries report the inner error's message and stack (plus React's
 * componentStack) through Faro's `context` field instead — see
 * components/Client500.tsx. `context` is passed through verbatim and the
 * stack-trace parser never touches it.
 */
export class CriticalApplicationError extends Error {
  public readonly origin: ErrorOrigin;
  public readonly timestamp: string;
  public readonly innerError?: Error;

  constructor(message: string, origin: ErrorOrigin, innerError?: Error) {
    super(message);
    this.name = 'CriticalApplicationError';
    this.origin = origin;
    this.timestamp = new Date().toISOString();
    this.innerError = innerError;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CriticalApplicationError);
    }
  }
}

/**
 * Recoverable sibling of CriticalApplicationError — same contract, including
 * why `innerError` never touches `this.stack`.
 */
export class RecoverableCriticalApplicationError extends Error {
  public readonly origin: ErrorOrigin;
  public readonly timestamp: string;
  public readonly innerError?: Error;

  constructor(message: string, origin: ErrorOrigin, innerError?: Error) {
    super(message);
    this.name = 'RecoverableCriticalApplicationError';
    this.origin = origin;
    this.timestamp = new Date().toISOString();
    this.innerError = innerError;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RecoverableCriticalApplicationError);
    }
  }
}
